import { useSnackbar } from '@/hooks/common/useSnackbar'
import { DragDropContext, Draggable, DropResult, Droppable } from '@hello-pangea/dnd'
import {
  Add,
  ArrowDownward,
  ArrowUpward,
  CloudUpload,
  Delete,
  DragHandle,
  PhotoCamera,
  PlayCircle,
  TextFields,
  Videocam,
  VisibilityOff
} from '@mui/icons-material'
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  IconButton,
  Paper,
  Stack,
  Switch,
  Tab,
  Tabs,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
  alpha
} from '@mui/material'
import { PhotoTaskTemplateItem } from '@otw/models/photos'
import { ConfirmDialog } from 'components/ConfirmDialog'
import { usePhotoTaskTemplates } from 'hooks/admin/usePhotoTaskTemplates'
import React, { useEffect, useState } from 'react'

interface PhotoTemplateConfigDialogProps {
  open: boolean
  onClose: () => void
  companyId?: string
}

type FlowType = 'preDelivery' | 'postDelivery' | 'guest'

export const PhotoTemplateConfigDialog: React.FC<PhotoTemplateConfigDialogProps> = ({
  open,
  onClose,
  companyId,
}) => {
  const { showSuccess, showError } = useSnackbar()
  const [activeFlow, setActiveFlow] = useState<FlowType>('preDelivery')
  const [editingTasks, setEditingTasks] = useState<PhotoTaskTemplateItem[]>([])
  const [hasChanges, setHasChanges] = useState(false)
  const [showConfirmDialog, setShowConfirmDialog] = useState(false)
  const [selectedTaskIndex, setSelectedTaskIndex] = useState<number>(0)
  
  // Hide guest tab when opened from a company (companyId is provided)
  const showGuestTab = !companyId

  const {
    templates,
    isLoading,
    getTemplateByFlow,
    createTemplate,
    updateTemplateTasks,
    uploadPhotoForTask,
    uploadVideoForTask,
    refetch,
  } = usePhotoTaskTemplates()

  // Load template for active flow when dialog opens or flow/company changes
  useEffect(() => {
    if (!open) return
    
    // If guest tab is hidden and activeFlow is guest, switch to preDelivery
    if (!showGuestTab && activeFlow === 'guest') {
      setActiveFlow('preDelivery')
      return
    }
    
    const template = getTemplateByFlow(activeFlow, companyId)
    if (template) {
      setEditingTasks([...template.tasks])
      setHasChanges(false)
    } else {
      // Template doesn't exist, create empty
      setEditingTasks([])
      setHasChanges(false)
    }
    setSelectedTaskIndex(0) // Reset to first task when switching flows
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeFlow, open, companyId, showGuestTab]) // Only depend on flow/company/dialog state, not templates
  
  // Update from templates when they change (but only if no unsaved local changes)
  useEffect(() => {
    if (!open || hasChanges) return
    
    const template = getTemplateByFlow(activeFlow, companyId)
    if (template && template.tasks.length > 0) {
      // Only update if the template actually has tasks (to avoid clearing during initial load)
      setEditingTasks([...template.tasks])
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [templates, open, activeFlow, companyId, hasChanges]) // Don't include getTemplateByFlow to avoid re-runs

  const handleFlowChange = (_event: React.SyntheticEvent, newFlow: FlowType) => {
    if (newFlow !== null) {
      // Prevent switching to guest if guest tab is hidden
      if (newFlow === 'guest' && !showGuestTab) {
        return
      }
      setActiveFlow(newFlow)
    }
  }

  const handleAddTask = async () => {
    const newTask: PhotoTaskTemplateItem = {
      id: `task-${Date.now()}`,
      displayName: 'New Task',
      order: editingTasks.length,
      active: false,
      mediaType: 'photo',
      placeholderText: 'Enter instructions for this task',
    }
    
    const updatedTasks = [...editingTasks, newTask]
    setEditingTasks(updatedTasks)
    setSelectedTaskIndex(updatedTasks.length - 1) // Select the newly added task
    
    // Auto-save the new task immediately
    try {
      const templateId = companyId ? `${activeFlow}-${companyId}` : `${activeFlow}-default`
      const existingTemplate = getTemplateByFlow(activeFlow, companyId)
      
      // Check if we're editing a default template (fallback) for a company
      const isDefaultTemplate = companyId && existingTemplate && (
        !existingTemplate.company_id || 
        existingTemplate.id === `${activeFlow}-default`
      )
      
      if (!existingTemplate || isDefaultTemplate) {
        // Create new company-specific template
        await createTemplate.mutateAsync({
          id: templateId,
          name: `${activeFlow.charAt(0).toUpperCase() + activeFlow.slice(1)} Photo Tasks`,
          tasks: updatedTasks,
          company_id: companyId,
        })
      } else {
        await updateTemplateTasks.mutateAsync({
          templateId,
          tasks: updatedTasks,
        })
      }
      
      showSuccess('Task added successfully')
      await refetch()
    } catch (error) {
      showError('Failed to add task: ' + (error as Error).message)
    }
  }

  const handleDragEnd = async (result: DropResult) => {
    if (!result.destination) return

    const items = Array.from(editingTasks)
    const [reorderedItem] = items.splice(result.source.index, 1)
    items.splice(result.destination.index, 0, reorderedItem)

    // Update order numbers
    const updatedTasks = items.map((task, index) => ({
      ...task,
      order: index
    }))

    setEditingTasks(updatedTasks)
    setHasChanges(true)

    // Update selected index if it was moved
    if (selectedTaskIndex === result.source.index) {
      setSelectedTaskIndex(result.destination.index)
    } else if (selectedTaskIndex >= Math.min(result.source.index, result.destination.index) && 
               selectedTaskIndex <= Math.max(result.source.index, result.destination.index)) {
      // Adjust selected index if it's in the affected range
      if (result.source.index < result.destination.index && selectedTaskIndex <= result.destination.index) {
        setSelectedTaskIndex(selectedTaskIndex - 1)
      } else if (result.source.index > result.destination.index && selectedTaskIndex >= result.destination.index) {
        setSelectedTaskIndex(selectedTaskIndex + 1)
      }
    }

    // Auto-save the reordered tasks
    try {
      const templateId = companyId ? `${activeFlow}-${companyId}` : `${activeFlow}-default`
      const existingTemplate = getTemplateByFlow(activeFlow, companyId)
      
      // Check if we're editing a default template (fallback) for a company
      const isDefaultTemplate = companyId && existingTemplate && (
        !existingTemplate.company_id || 
        existingTemplate.id === `${activeFlow}-default`
      )
      
      if (!existingTemplate || isDefaultTemplate) {
        // Create new company-specific template
        await createTemplate.mutateAsync({
          id: templateId,
          name: `${activeFlow.charAt(0).toUpperCase() + activeFlow.slice(1)} Photo Tasks`,
          tasks: updatedTasks,
          company_id: companyId,
        })
        showSuccess('Task order updated')
        await refetch()
      } else {
        await updateTemplateTasks.mutateAsync({
          templateId,
          tasks: updatedTasks,
        })
        showSuccess('Task order updated')
        await refetch()
      }
    } catch (error) {
      showError('Failed to update task order: ' + (error as Error).message)
    }
  }

  const handleUpdateTask = (index: number, updates: Partial<PhotoTaskTemplateItem>) => {
    const updatedTasks = [...editingTasks]
    updatedTasks[index] = { ...updatedTasks[index], ...updates }
    setEditingTasks(updatedTasks)
    setHasChanges(true)
  }

  const handleMoveUp = (index: number) => {
    if (index === 0) return
    const updatedTasks = [...editingTasks]
    const temp = updatedTasks[index]
    updatedTasks[index] = updatedTasks[index - 1]
    updatedTasks[index - 1] = temp
    // Update order numbers
    updatedTasks.forEach((task, idx) => {
      task.order = idx
    })
    setEditingTasks(updatedTasks)
    setHasChanges(true)
    // Update selected index if it was the moved task
    if (selectedTaskIndex === index) {
      setSelectedTaskIndex(index - 1)
    } else if (selectedTaskIndex === index - 1) {
      setSelectedTaskIndex(index)
    }
  }

  const handleMoveDown = (index: number) => {
    if (index === editingTasks.length - 1) return
    const updatedTasks = [...editingTasks]
    const temp = updatedTasks[index]
    updatedTasks[index] = updatedTasks[index + 1]
    updatedTasks[index + 1] = temp
    // Update order numbers
    updatedTasks.forEach((task, idx) => {
      task.order = idx
    })
    setEditingTasks(updatedTasks)
    setHasChanges(true)
    // Update selected index if it was the moved task
    if (selectedTaskIndex === index) {
      setSelectedTaskIndex(index + 1)
    } else if (selectedTaskIndex === index + 1) {
      setSelectedTaskIndex(index)
    }
  }

  const handlePhotoUpload = async (index: number, file: File) => {
    try {
      const templateId = companyId ? `${activeFlow}-${companyId}` : `${activeFlow}-default`
      const task = editingTasks[index]
      const url = await uploadPhotoForTask(templateId, task.id, file)
      
      // Update local state immediately so the photo appears right away
      const updatedTasks = [...editingTasks]
      updatedTasks[index] = { ...updatedTasks[index], storagePath: url }
      setEditingTasks(updatedTasks)
      
      // Save the updated task to Firestore immediately
      const existingTemplate = getTemplateByFlow(activeFlow, companyId)
      
      // Check if we're editing a default template (fallback) for a company
      const isDefaultTemplate = companyId && existingTemplate && (
        !existingTemplate.company_id || 
        existingTemplate.id === `${activeFlow}-default`
      )
      
      if (!existingTemplate || isDefaultTemplate) {
        // Template doesn't exist yet or is a default template, create company-specific one
        await createTemplate.mutateAsync({
          id: templateId,
          name: `${activeFlow.charAt(0).toUpperCase() + activeFlow.slice(1)} Photo Tasks`,
          tasks: updatedTasks,
          company_id: companyId,
        })
        // Refetch to get the latest data, then mark as saved
        await refetch()
        setHasChanges(false)
        showSuccess('Photo uploaded successfully')
      } else {
        await updateTemplateTasks.mutateAsync({
          templateId,
          tasks: updatedTasks,
        })
        // Refetch to get the latest data, then mark as saved
        await refetch()
        setHasChanges(false)
        showSuccess('Photo uploaded successfully')
      }
    } catch (error) {
      showError('Failed to upload photo: ' + (error as Error).message)
    }
  }


  const handleSave = async () => {
    try {
      const templateId = companyId ? `${activeFlow}-${companyId}` : `${activeFlow}-default`
      const existingTemplate = getTemplateByFlow(activeFlow, companyId)
      
      // Check if we're editing a default template (fallback) for a company
      const isDefaultTemplate = companyId && existingTemplate && (
        !existingTemplate.company_id || 
        existingTemplate.id === `${activeFlow}-default`
      )
      
      // If template doesn't exist or is a default template, create company-specific one
      if (!existingTemplate || isDefaultTemplate) {
        await createTemplate.mutateAsync({
          id: templateId,
          name: `${activeFlow.charAt(0).toUpperCase() + activeFlow.slice(1)} Photo Tasks`,
          tasks: editingTasks,
          company_id: companyId,
        })
      } else {
        // Update existing company-specific template tasks
        await updateTemplateTasks.mutateAsync({
          templateId,
          tasks: editingTasks,
        })
      }
      
      showSuccess('Template saved successfully')
      setHasChanges(false)
      await refetch()
    } catch (error) {
      showError('Failed to save template: ' + (error as Error).message)
    }
  }

  const handleClose = () => {
    if (hasChanges) {
      setShowConfirmDialog(true)
    } else {
      onClose()
    }
  }

  const handleConfirmClose = () => {
    setShowConfirmDialog(false)
    setHasChanges(false)
    onClose()
  }

  return (
    <Dialog 
      open={open} 
      onClose={handleClose} 
      maxWidth="lg" 
      fullWidth
      sx={{
        '& .MuiDialog-paper': {
          height: '90vh',
          maxHeight: '90vh',
        }
      }}
    >
      <DialogTitle sx={{ 
        bgcolor: (theme) => alpha(theme.palette.primary.main, 0.05),
        borderBottom: 1,
        borderColor: 'divider',
        flexShrink: 0
      }}>
        <Stack direction="row" alignItems="center" spacing={2}>
          <PhotoCamera color="primary" />
          <Typography variant="h5" component="div" sx={{ fontWeight: 600 }}>
            Configure Photo Templates
          </Typography>
        </Stack>
      </DialogTitle>
      <DialogContent sx={{ 
        display: 'flex', 
        flexDirection: 'column', 
        overflow: 'hidden',
        p: 3
      }}>
        <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2, flexShrink: 0 }}>
          <Tabs value={activeFlow} onChange={handleFlowChange}>
            <Tab label="Pre-Delivery" value="preDelivery" />
            <Tab label="Post-Delivery" value="postDelivery" />
            {showGuestTab && <Tab label="Guest" value="guest" />}
          </Tabs>
        </Box>

        {hasChanges && (
          <Alert severity="info" sx={{ mb: 2, flexShrink: 0 }}>
            You have unsaved changes. Click Save to apply them.
          </Alert>
        )}

        <Stack direction="row" spacing={3} sx={{ flexGrow: 1, overflow: 'hidden' }}>
          {/* Left Sidebar - Task List */}
          <Box sx={{ width: 280, flexShrink: 0, display: 'flex', flexDirection: 'column', height: '100%' }}>
            <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, mb: 2, flexShrink: 0 }}>
              Tasks ({editingTasks.length})
            </Typography>
            
            {isLoading ? (
              <Box sx={{ flexGrow: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <Typography>Loading...</Typography>
              </Box>
            ) : (
              <Box sx={{ flexGrow: 1, overflow: 'auto', mb: 2 }}>
                {editingTasks.length > 0 ? (
                  <DragDropContext onDragEnd={handleDragEnd}>
                    <Droppable droppableId="task-list">
                      {(provided, snapshot) => (
                        <Box
                          {...provided.droppableProps}
                          ref={provided.innerRef}
                          sx={{
                            bgcolor: snapshot.isDraggingOver ? (theme) => alpha(theme.palette.primary.main, 0.05) : 'transparent',
                            borderRadius: 2,
                            transition: 'background-color 0.2s ease-in-out',
                            minHeight: 100
                          }}
                        >
                        <Stack spacing={1}>
                          {editingTasks.map((task, index) => (
                          <Draggable key={task.id} draggableId={task.id} index={index}>
                            {(provided, snapshot) => (
                              <Card
                                ref={provided.innerRef}
                                {...provided.draggableProps}
                                variant={selectedTaskIndex === index ? "elevation" : "outlined"}
                                elevation={selectedTaskIndex === index ? 3 : snapshot.isDragging ? 4 : 0}
                                sx={{
                                  cursor: 'pointer',
                                  transition: 'all 0.2s ease-in-out',
                                  border: selectedTaskIndex === index ? '2px solid' : '1px solid',
                                  borderColor: selectedTaskIndex === index ? 'primary.main' : 'divider',
                                  bgcolor: selectedTaskIndex === index ? (theme) => alpha(theme.palette.primary.main, 0.05) : 
                                           snapshot.isDragging ? 'background.paper' : 'transparent',
                                  opacity: task.active === false ? 0.6 : 1,
                                  transform: snapshot.isDragging ? 'rotate(3deg)' : 'none',
                                  boxShadow: snapshot.isDragging ? '0 8px 16px rgba(0,0,0,0.15)' : undefined,
                                  '&:hover': {
                                    borderColor: 'primary.main',
                                    bgcolor: (theme) => alpha(theme.palette.primary.main, 0.03)
                                  }
                                }}
                                onClick={() => setSelectedTaskIndex(index)}
                              >
                                <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                                  <Stack direction="row" alignItems="center" spacing={2}>
                                    {/* Drag Handle */}
                                    <Box
                                      {...provided.dragHandleProps}
                                      sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        cursor: 'grab',
                                        '&:active': { cursor: 'grabbing' },
                                        color: 'text.secondary',
                                        '&:hover': { color: 'primary.main' }
                                      }}
                                    >
                                      <DragHandle fontSize="small" />
                                    </Box>
                                    
                                    <Chip 
                                      label={`#${index + 1}`} 
                                      size="small" 
                                      color={selectedTaskIndex === index ? "primary" : "default"}
                                      variant={selectedTaskIndex === index ? "filled" : "outlined"}
                                    />
                                    
                                    <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                                      <Stack direction="row" alignItems="center" spacing={1}>
                                        <Typography 
                                          variant="body2" 
                                          fontWeight={selectedTaskIndex === index ? 600 : 400}
                                          noWrap
                                          sx={{ flexGrow: 1 }}
                                        >
                                          {task.displayName}
                                        </Typography>
                                        {task.active === false && (
                                          <Box
                                            sx={{
                                              display: 'flex',
                                              alignItems: 'center',
                                              bgcolor: (theme) => alpha(theme.palette.warning.main, 0.1),
                                              color: 'warning.main',
                                              borderRadius: '50%',
                                              width: 20,
                                              height: 20,
                                              justifyContent: 'center'
                                            }}
                                          >
                                            <VisibilityOff sx={{ fontSize: 12 }} />
                                          </Box>
                                        )}
                                      </Stack>
                                      <Stack direction="row" spacing={0.5} sx={{ mt: 0.5 }}>
                                        <Chip
                                          icon={task.mediaType === 'photo' ? <PhotoCamera /> : <Videocam />}
                                          label={task.mediaType}
                                          size="small"
                                          variant="outlined"
                                          sx={{ height: 20, fontSize: '0.7rem' }}
                                        />
                                      </Stack>
                                    </Box>

                                    {/* Media indicator */}
                                    <Box>
                                      {task.mediaType === 'photo' ? (
                                        // Photo tasks - show image or upload icon
                                        task.storagePath ? (
                                          <Box
                                            sx={{
                                              width: 32,
                                              height: 32,
                                              borderRadius: 1,
                                              overflow: 'hidden',
                                              border: 1,
                                              borderColor: 'divider',
                                            }}
                                          >
                                            <img
                                              src={task.storagePath}
                                              alt="Thumbnail"
                                              style={{
                                                width: '100%',
                                                height: '100%',
                                                objectFit: 'cover'
                                              }}
                                            />
                                          </Box>
                                        ) : (
                                          <Box
                                            sx={{
                                              width: 32,
                                              height: 32,
                                              borderRadius: 1,
                                              bgcolor: 'grey.100',
                                              border: 1,
                                              borderStyle: 'dashed',
                                              borderColor: 'divider',
                                              display: 'flex',
                                              alignItems: 'center',
                                              justifyContent: 'center'
                                            }}
                                          >
                                            <CloudUpload sx={{ fontSize: 16, color: 'grey.400' }} />
                                          </Box>
                                        )
                                      ) : (
                                        // Video tasks - show text icon (no upload needed)
                                        <Box
                                          sx={{
                                            width: 32,
                                            height: 32,
                                            borderRadius: 1,
                                            bgcolor: (theme) => alpha(theme.palette.info.main, 0.1),
                                            border: '1px solid',
                                            borderColor: 'info.light',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center'
                                          }}
                                        >
                                          <TextFields sx={{ fontSize: 16, color: 'info.main' }} />
                                        </Box>
                                      )}
                                    </Box>
                                  </Stack>
                                </CardContent>
                              </Card>
                            )}
                          </Draggable>
                          ))}
                          {provided.placeholder}
                        </Stack>
                      </Box>
                    )}
                  </Droppable>
                </DragDropContext>
                ) : (
                  <Box sx={{ 
                    display: 'flex', 
                    flexDirection: 'column',
                    alignItems: 'center', 
                    justifyContent: 'center',
                    height: '100%',
                    color: 'text.secondary',
                    textAlign: 'center'
                  }}>
                    <Typography variant="body1" gutterBottom>
                      No tasks yet
                    </Typography>
                    <Typography variant="body2">
                      Click "Add New Task" to get started
                    </Typography>
                  </Box>
                )}
              </Box>
            )}

            {/* Add Task Button at Bottom - Always Visible */}
            <Button
              variant="contained"
              startIcon={<Add />}
              onClick={handleAddTask}
              fullWidth
              disabled={isLoading}
              sx={{ 
                borderRadius: 2,
                textTransform: 'none',
                fontWeight: 600,
                mt: 'auto',
                flexShrink: 0
              }}
            >
              Add New Task
            </Button>
          </Box>

            {/* Right Side - Selected Task Detail */}
            <Box sx={{ flexGrow: 1, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
              {editingTasks.length > 0 && selectedTaskIndex < editingTasks.length && (
                <Card 
                  elevation={2}
                  sx={{ 
                    borderRadius: 3,
                    overflow: 'hidden',
                    border: 1,
                    borderStyle: editingTasks[selectedTaskIndex]?.active === false ? 'dashed' : 'solid',
                    borderColor: 'divider',
                    opacity: editingTasks[selectedTaskIndex]?.active === false ? 0.7 : 1,
                    display: 'flex',
                    flexDirection: 'column',
                    height: '100%'
                  }}
                >
                  {/* Header with drag handle and controls */}
                  <Box sx={{ 
                    bgcolor: (theme) => alpha(theme.palette.primary.main, 0.05),
                    p: 2,
                    borderBottom: 1,
        borderColor: 'divider',
                    flexShrink: 0
                  }}>
                    <Stack direction="row" alignItems="center" spacing={2}>
                      <Chip 
                        label={`#${selectedTaskIndex + 1}`} 
                        size="small" 
                        color="primary" 
                        variant="outlined"
                      />
                      
                      <TextField
                        key={`task-name-${selectedTaskIndex}-${editingTasks[selectedTaskIndex]?.id}`}
                        value={editingTasks[selectedTaskIndex]?.displayName || ''}
                        onChange={(e) => {
                          const newValue = e.target.value
                          handleUpdateTask(selectedTaskIndex, { displayName: newValue })
                        }}
                        variant="standard"
                        sx={{ 
                          flexGrow: 1,
                          '& .MuiInput-root': {
                            fontSize: '1.1rem',
                            fontWeight: 600
                          }
                        }}
                        placeholder="Task name..."
                      />

                      <Stack direction="row" spacing={1}>
                        <IconButton
                          size="small"
                          onClick={() => handleMoveUp(selectedTaskIndex)}
                          disabled={selectedTaskIndex === 0}
                          sx={{ 
                            bgcolor: 'background.paper',
                            '&:hover': { bgcolor: 'primary.light', color: 'white' }
                          }}
                        >
                          <ArrowUpward fontSize="small" />
                        </IconButton>
                        <IconButton
                          size="small"
                          onClick={() => handleMoveDown(selectedTaskIndex)}
                          disabled={selectedTaskIndex === editingTasks.length - 1}
                          sx={{ 
                            bgcolor: 'background.paper',
                            '&:hover': { bgcolor: 'primary.light', color: 'white' }
                          }}
                        >
                          <ArrowDownward fontSize="small" />
                        </IconButton>
                      </Stack>

                      <FormControlLabel
                        control={
                          <Switch
                            checked={editingTasks[selectedTaskIndex]?.active !== false}
                            onChange={(e) =>
                              handleUpdateTask(selectedTaskIndex, { active: e.target.checked })
                            }
                            color="primary"
                          />
                        }
                        label="Active"
                        sx={{ ml: 1 }}
                      />
                    </Stack>
                  </Box>

                  <CardContent sx={{ p: 2, flexGrow: 1, overflow: 'auto' }}>
                    {/* Media Type Toggle */}
                    <Stack direction="row" spacing={1} sx={{ mb: 2 }}>
                      <ToggleButtonGroup
                        value={editingTasks[selectedTaskIndex]?.mediaType || 'photo'}
                        exclusive
                        onChange={(_, value) => {
                          if (value !== null) {
                            handleUpdateTask(selectedTaskIndex, { 
                              mediaType: value,
                              placeholderText: value === 'photo' ? 'Upload example photo' : 'Record a video of...'
                            })
                          }
                        }}
                        size="small"
                      >
                        <ToggleButton value="photo" sx={{ px: 2, py: 0.5 }}>
                          <PhotoCamera sx={{ mr: 0.5, fontSize: 16 }} /> Photo
                        </ToggleButton>
                        <ToggleButton value="video" sx={{ px: 2, py: 0.5 }}>
                          <Videocam sx={{ mr: 0.5, fontSize: 16 }} /> Video
                        </ToggleButton>
                      </ToggleButtonGroup>
                    </Stack>

                    {/* For Photos: Media Display/Upload Area */}
                    {editingTasks[selectedTaskIndex]?.mediaType === 'photo' && (
                      <Paper
                        elevation={0}
                        sx={{
                          border: 2,
                          borderStyle: 'dashed',
                          borderColor: 'divider',
                          borderRadius: 2,
                          textAlign: 'center',
                          bgcolor: (theme) => alpha(theme.palette.grey[100], 0.5),
                          minHeight: 300,
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: 'center',
                          justifyContent: 'center',
                          position: 'relative',
                          overflow: 'hidden',
                          transition: 'all 0.2s ease-in-out',
                          '&:hover': {
                            borderColor: 'primary.main',
                            bgcolor: (theme) => alpha(theme.palette.primary.main, 0.05)
                          }
                        }}
                      >
                        {/* Show uploaded photo */}
                        {editingTasks[selectedTaskIndex]?.storagePath ? (
                          <Box sx={{ position: 'relative', width: '100%', height: '100%' }}>
                            <img
                              src={editingTasks[selectedTaskIndex]?.storagePath}
                              alt="Example"
                              style={{
                                width: '100%',
                                height: '300px',
                                objectFit: 'contain',
                                borderRadius: '8px',
                              }}
                            />
                            <IconButton
                              sx={{
                                position: 'absolute',
                                top: 8,
                                right: 8,
                                bgcolor: 'rgba(255,255,255,0.9)',
                                '&:hover': { bgcolor: 'error.light', color: 'white' }
                              }}
                              size="small"
                              onClick={() => handleUpdateTask(selectedTaskIndex, { storagePath: undefined })}
                            >
                              <Delete fontSize="small" />
                            </IconButton>
                          </Box>
                        ) : (
                          <>
                            <CloudUpload sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
                            <Typography variant="h6" color="text.secondary" gutterBottom>
                              Upload Example Photo
                            </Typography>
                            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                              This will be shown to transporters as a reference
                            </Typography>
                            
                            <Button
                              variant="contained"
                              component="label"
                              startIcon={<PhotoCamera />}
                              size="small"
                              sx={{ 
                                borderRadius: 1,
                                textTransform: 'none',
                                px: 2,
                                py: 1
                              }}
                            >
                              Choose Photo
                              <input
                                type="file"
                                hidden
                                accept="image/*"
                                onChange={(e) => {
                                  const file = e.target.files?.[0]
                                  if (file) {
                                    handlePhotoUpload(selectedTaskIndex, file)
                                  }
                                }}
                              />
                            </Button>
                          </>
                        )}
                      </Paper>
                    )}

                    {/* For Videos: Text Instructions Area */}
                    {editingTasks[selectedTaskIndex]?.mediaType === 'video' && (
                      <Paper
                        elevation={0}
                        sx={{
                          border: '2px solid',
                          borderColor: 'info.light',
                          borderRadius: 2,
                          bgcolor: (theme) => alpha(theme.palette.info.main, 0.05),
                          minHeight: 300,
                          p: 3,
                        }}
                      >
                        <Stack spacing={2}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                            <Videocam color="info" />
                            <Typography variant="h6" color="info.main" sx={{ fontWeight: 600 }}>
                              Video Recording Instructions
                            </Typography>
                          </Box>
                          
                          <Typography variant="body2" color="text.secondary" gutterBottom>
                            Enter instructions for what the transporter should record:
                          </Typography>
                          
                          <TextField
                            fullWidth
                            multiline
                            rows={8}
                            value={editingTasks[selectedTaskIndex]?.placeholderText || ''}
                            onChange={(e) =>
                              handleUpdateTask(selectedTaskIndex, { placeholderText: e.target.value })
                            }
                            placeholder="Example: Record a 360-degree walk-around of the vehicle, showing all angles, focusing on any damage or notable features..."
                            variant="outlined"
                            sx={{ 
                              bgcolor: 'background.paper',
                              '& .MuiOutlinedInput-root': {
                                '&:hover fieldset': {
                                  borderColor: 'info.main',
                                },
                              }
                            }}
                          />
                          
                          <Alert severity="info" sx={{ mt: 1 }}>
                            These instructions will be displayed to transporters when they need to record this video.
                          </Alert>
                        </Stack>
                      </Paper>
                    )}
                  </CardContent>
                </Card>
              )}
              
              {editingTasks.length === 0 && (
                <Box sx={{ textAlign: 'center', py: 4 }}>
                  <Typography variant="h6" color="text.secondary">
                    No tasks yet
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Click "Add New Task" to get started
                  </Typography>
                </Box>
              )}
            </Box>
          </Stack>
      </DialogContent>
      <DialogActions sx={{ 
        p: 3, 
        bgcolor: (theme) => alpha(theme.palette.grey[50], 0.5),
        borderTop: 1,
        borderColor: 'divider',
        flexShrink: 0
      }}>
        <Button 
          onClick={handleClose}
          sx={{ 
            borderRadius: 2,
            textTransform: 'none',
            fontWeight: 600,
            px: 3
          }}
        >
          Cancel
        </Button>
        <Button
          onClick={handleSave}
          variant="contained"
          disabled={!hasChanges || isLoading}
          sx={{ 
            borderRadius: 2,
            textTransform: 'none',
            fontWeight: 600,
            px: 4
          }}
        >
          {isLoading ? 'Saving...' : 'Save Changes'}
        </Button>
      </DialogActions>

      <ConfirmDialog
        open={showConfirmDialog}
        title="Unsaved Changes"
        message="You have unsaved changes. Are you sure you want to close without saving?"
        confirmText="Close Without Saving"
        cancelText="Continue Editing"
        onConfirm={handleConfirmClose}
        onCancel={() => setShowConfirmDialog(false)}
        severity="warning"
      />
    </Dialog>
  )
}

export default PhotoTemplateConfigDialog;